import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { InsightsFiltersOptionsQuery } from 'generated/resources/graphql'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Filters from 'modules/insights/components/Filters'
import { HeadcountTable } from 'modules/insights/components/HeadcountTable'
import ChartHeadcountEvolution from 'modules/insights/components/WidgetHeadcountEvolution/ChartHeadcountEvolution'
import { HeadcountCounters } from 'modules/insights/components/WidgetHeadcountEvolution/HeadcountCounters'
import { CycleDateRangeWithSetters } from 'modules/insights/hooks/useCycleDateRange'
import { SubRangeSelectionAction } from 'modules/insights/lib/events'
import type HeadcountEvolution from 'modules/insights/lib/metrics/headcountEvolution'
import { useInsightsContext } from 'modules/insights/pages/RootPage'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useHeadcountPageQuery } from './useHeadcountPageQuery'
import { useSelectedRange } from './useSelectedRange'

export const SELECTED_RANGE_PARAM = 'selectedRange'

type PageContentProps = {
  headcountEvolution: HeadcountEvolution
  filterOptionsData: InsightsFiltersOptionsQuery
  cycleDateRange: CycleDateRangeWithSetters
  isLoading: boolean
}

const PageContent = ({
  headcountEvolution,
  filterOptionsData,
  cycleDateRange,
  isLoading,
  users,
}: PageContentProps & { users: typeof import('modules/core/store/collections/users').default }) => {
  const { filters, setFilter, clearFilters } = useInsightsContext()

  const [showExportModal, setShowExportModal] = React.useState(false)

  const { selectedRange, setSelectedRange } = useSelectedRange(headcountEvolution)

  const headcountEvolutionData = headcountEvolution.generateHeadcountEvolution()

  const handleXAxisSelected = useUnsafeCallback(
    (date: Date, action: SubRangeSelectionAction) => {
      track(trackingEvents.insights.headcountPage.subRangeSelected, {
        action,
      })

      setSelectedRange(date)
    },
    [setSelectedRange]
  )

  return (
    <Box flexDirection="column" gap="s48">
      <Box flexDirection="row" gap="s16" justifyContent="spaceBetween" flexWrap="wrap">
        <Filters
          filters={filters}
          setFilter={setFilter}
          isLoading={isLoading}
          clearFilters={clearFilters}
          filterOptionsData={filterOptionsData}
          users={users}
        />
        <Box flexDirection="row" gap="s8">
          <DateNavigator
            cycle={cycleDateRange.cycle}
            startDate={cycleDateRange.startDate}
            endDate={cycleDateRange.endDate}
            onCycleChange={cycleDateRange.setCycle}
            onDateRangeChange={cycleDateRange.setDateRange}
          />
          <Button type="primary" iconName="download" onClick={() => setShowExportModal(true)}>
            {i18n.t('common.export')}
          </Button>
        </Box>
      </Box>

      <Box flexDirection="row" gap="s40" paddingX="s32" alignItems="center">
        <HeadcountCounters headcountEvolution={headcountEvolution} date={selectedRange} />

        <Box minWidth="none" flexGrow>
          <ChartHeadcountEvolution
            cycle={cycleDateRange.cycle}
            headcount={headcountEvolutionData}
            headcountEvolution={headcountEvolution.headcountEvolution}
            onXAxisSelected={handleXAxisSelected}
            selectedXAxis={selectedRange}
          />
        </Box>
      </Box>

      <HeadcountTable
        data={headcountEvolution.generateHeadcountEvolution()}
        headcountEvolution={headcountEvolution.headcountEvolution}
        joiners={headcountEvolution.joiners}
        leavers={headcountEvolution.leavers}
        selectedRange={selectedRange}
        showExportModal={showExportModal}
        onChangeSelectedRange={(newSelectedRange) =>
          handleXAxisSelected(newSelectedRange, 'date-range-navigator')
        }
        onCloseExportModal={() => setShowExportModal(false)}
      />
    </Box>
  )
}

const HeadcountPage = () => {
  const { filterOptionsData, filterOptionsError, users } = useInsightsContext()
  const { headcountEvolution, loading, error, cycleDateRange } = useHeadcountPageQuery()

  return (
    <ResponsivePage.Body
      loading={!headcountEvolution}
      error={error ?? filterOptionsError}
      variant="full"
    >
      {headcountEvolution && filterOptionsData && (
        <PageContent
          headcountEvolution={headcountEvolution}
          filterOptionsData={filterOptionsData}
          cycleDateRange={cycleDateRange}
          isLoading={loading}
          users={users}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default HeadcountPage
